import { useId } from 'react';
import { ActionIcon, type ActionIconProps, Tooltip, VisuallyHidden } from '@mantine/core';
import { IconTrash } from '@tabler/icons-react';

const FIRM_SHARED_DELETE_EXPLANATION =
  'Unshare it before deleting it, or switch off Enabled to stop your firm using it.';

type SkillDeleteButtonProps = {
  label: string;
  isShared: boolean;
  onDelete: () => void;
  /** Why a shared skill cannot be deleted, where the default does not fit the screen. */
  sharedExplanation?: string;
  size?: ActionIconProps['size'];
  iconSize?: number;
};

/** Delete for a company skill. A shared skill cannot be deleted, so its button says what to do
 * instead; the server refuses the delete too. */
export default function SkillDeleteButton({
  label,
  isShared,
  onDelete,
  sharedExplanation = FIRM_SHARED_DELETE_EXPLANATION,
  size,
  iconSize = 16,
}: SkillDeleteButtonProps) {
  const explanationId = useId();

  return (
    // aria-disabled, not disabled: a natively disabled button cannot take focus, so keyboard and
    // screen reader users would never reach the reason.
    <Tooltip
      label={sharedExplanation}
      disabled={!isShared}
      withArrow
      events={{ hover: true, focus: true, touch: false }}
    >
      <ActionIcon
        variant="subtle"
        color="red"
        size={size}
        aria-label={`Delete ${label}`}
        aria-disabled={isShared}
        aria-describedby={isShared ? explanationId : undefined}
        data-disabled={isShared || undefined}
        onClick={isShared ? undefined : onDelete}
      >
        <IconTrash size={iconSize} />
        {isShared && <VisuallyHidden id={explanationId}>{sharedExplanation}</VisuallyHidden>}
      </ActionIcon>
    </Tooltip>
  );
}
